/**
 * What the client is allowed to know about a setting.
 *
 * Note what is NOT here: the value of a secret. The server sends `isSet` and
 * nothing else for those. A console that displays the SMTP password to whoever
 * is looking over the owner's shoulder would be worse than no console.
 */

export type SettingType =
  | 'string' | 'int' | 'bool' | 'enum'
  | 'url' | 'cron' | 'list' | 'time-range' | 'path';

export type SettingGroup =
  | 'app' | 'auth' | 'storage' | 'sources' | 'schedule'
  | 'sla' | 'notify' | 'channels' | 'infra';

export const GROUP_LABELS: Record<SettingGroup, string> = {
  app:      'Приложение',
  auth:     'Вход и ключи',
  storage:  'Хранилища',
  sources:  'Источники бэкапов',
  schedule: 'Расписание сборщиков',
  sla:      'Пороги свежести',
  notify:   'Уведомления',
  channels: 'Каналы доставки',
  infra:    'Инфраструктура',
};

export const GROUP_ORDER: SettingGroup[] = [
  'notify', 'channels', 'sources', 'storage', 'schedule', 'sla', 'auth', 'app', 'infra',
];

/** Where the effective value came from. */
export type SettingSource = 'mongo' | 'env' | 'default';

export interface SettingDescriptor {
  key: string;
  group: SettingGroup;
  label: string;
  hint?: string;
  type: SettingType;
  options?: string[];
  secret: boolean;
  /** Which service actually reads it. */
  scope: 'app' | 'worker' | 'both';
  /** Non-empty when the value can only be changed in .env; the text says why. */
  envOnly?: string;
  /** True when a change takes effect without recreating containers. */
  live: boolean;

  // ── current state ───────────────────────────────────────────────────────
  /** Non-secret effective value. Always absent for secrets. */
  value?: string;
  /** What the code falls back to when nothing is configured. Never a secret. */
  defaultValue?: string;
  /** What .env says, shown so «вернуть из .env» is not a leap of faith. */
  envValue?: string;
  /** For secrets: whether .env holds anything, without saying what. */
  envIsSet: boolean;
  /** For secrets: is there an effective value at all. */
  isSet: boolean;
  source: SettingSource;
  updatedAt?: string;
  updatedBy?: string;
}

export interface SettingsSnapshot {
  items: SettingDescriptor[];
  /** Settings changed in the console but needing a container restart. */
  pendingRestart: string[];
}
